import { API_CONFIG } from '@/config/api';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import { normalizeDateString } from '@/utils/dateFormat';
import {
  buildCustomerSurveyReportHtml,
  type CustomerSurveyItem,
  type CustomerSurveyTemplateOptions,
} from '@/reports/templates/customerSurveyTemplate';

interface RawCustomerSurveyRecord {
  id?: string;
  transaction_date?: string | null;
  user_email?: string | null;
  customer_name?: string | null;
  missing_equipment_response?: string | null;
  missing_equipment_reason?: string | null;
  missing_equipment_details?: string | null;
  service_satisfaction?: string | null;
  staff_helpfulness?: string | null;
  process_efficiency?: string | null;
}

const toSeconds = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const date = new Date(normalizeDateString(value));
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.floor(date.getTime() / 1000);
};

export const runCustomerSurveyReport = async (params: Record<string, unknown>): Promise<void> => {
  const fromDate = (params.fromDate as string | undefined) ?? '';
  const toDate = (params.toDate as string | undefined) ?? '';
  const userEmail = (params.userEmail as string | undefined) ?? '';

  const endpoint = `${API_CONFIG.DATA_API_BASE_PATH}/feedback`;
  const records = await httpClient.get<RawCustomerSurveyRecord[]>(endpoint);

  const fromTs = fromDate ? toSeconds(fromDate) : null;
  const toTs = toDate ? toSeconds(toDate) : null;

  const filtered = records.filter((record) => {
    const ts = toSeconds(record.transaction_date ?? null);
    if (ts == null) return false;
    if (fromTs != null && ts < fromTs) return false;
    if (toTs != null && ts > toTs + 24 * 3600 - 1) return false;
    if (userEmail && !(record.user_email ?? '').toLowerCase().includes(userEmail.toLowerCase())) {
      return false;
    }
    return true;
  });

  const items: CustomerSurveyItem[] = filtered.map((record) => ({
    transactionDate: record.transaction_date ?? '',
    user: record.user_email ?? '',
    customer: record.customer_name ?? '',
    missingEquipmentResponse: record.missing_equipment_response ?? '',
    missingEquipmentReason: record.missing_equipment_reason ?? '',
    missingEquipmentDetails: record.missing_equipment_details ?? '',
    serviceSatisfaction: record.service_satisfaction ?? '',
    staffHelpfulness: record.staff_helpfulness ?? '',
    processEfficiency: record.process_efficiency ?? '',
  }));

  const options: CustomerSurveyTemplateOptions = {
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
      userEmail: userEmail || null,
    },
    items,
  };

  const html = buildCustomerSurveyReportHtml(options);
  outputReport(
    html,
    'customer-survey-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
